'use client';

import * as React from 'react';
import { Inbox } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '../theme';
import type { EmptyStateProps } from './EmptyState.types';

const emptyStateVariants = cva(
  'flex flex-col items-center justify-center text-center',
  {
    variants: {
      size: {
        sm: 'py-6 px-4',
        md: 'py-12 px-6',
        lg: 'py-20 px-8',
      },
      variant: {
        default: '',
        card: 'bg-white rounded-xl border border-warm-gray/10 shadow-soft',
        inline: 'bg-cream rounded-lg',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  }
);

const iconSizeMap = {
  sm: 'w-10 h-10',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
};

const titleSizeMap = {
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-xl',
};

const descriptionSizeMap = {
  sm: 'text-sm max-w-xs',
  md: 'text-sm max-w-sm',
  lg: 'text-base max-w-md',
};

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (props, ref) => {
    const {
      icon: Icon = Inbox,
      title,
      description,
      action,
      secondaryAction,
      className,
      size = 'md',
      variant = 'default',
    } = props;

    return (
      <div
        ref={ref}
        className={cn(emptyStateVariants({ size, variant }), className)}
      >
        <div
          className={cn(
            'rounded-full bg-cream-dark flex items-center justify-center mb-4',
            size === 'sm' && 'p-3',
            size === 'md' && 'p-4',
            size === 'lg' && 'p-5'
          )}
        >
          <Icon className={cn('text-warm-gray', iconSizeMap[size])} />
        </div>

        <h3
          className={cn(
            'font-semibold text-navy font-display',
            titleSizeMap[size]
          )}
        >
          {title}
        </h3>

        {description && (
          <p
            className={cn(
              'mt-2 text-warm-gray',
              descriptionSizeMap[size]
            )}
          >
            {description}
          </p>
        )}

        {(action || secondaryAction) && (
          <div className={cn('flex items-center gap-3', size === 'sm' ? 'mt-4' : 'mt-6')}>
            {action}
            {secondaryAction}
          </div>
        )}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

export default EmptyState;
